/* ==========================================================================
   Main Application JavaScript - The Arcanopolis Archive
   ========================================================================== */

// Global application state
const App = {
    data: new Map(),
    searchIndex: null,
    currentTheme: 'light',
    currentPage: 'home',
    
    // Initialize the application
    init() {
        this.setupEventListeners();
        this.loadTheme();
        this.loadData();
        this.initializePage();
    },
    
    // Set up global event listeners
    setupEventListeners() {
        // Theme toggle
        const themeToggle = document.getElementById('themeToggle');
        if (themeToggle) {
            themeToggle.addEventListener('click', () => this.toggleTheme());
        }
        
        // Hero search
        const heroSearch = document.getElementById('heroSearch');
        const heroSearchBtn = document.getElementById('heroSearchBtn');
        if (heroSearch && heroSearchBtn) {
            heroSearchBtn.addEventListener('click', () => this.performHeroSearch());
            heroSearch.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') this.performHeroSearch();
            });
        }
        
        // Global keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            // Ctrl/Cmd + K for search
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                this.focusSearch();
            }
        });
    },
    
    // Load theme from localStorage
    loadTheme() {
        const savedTheme = localStorage.getItem('arcanopolis-theme') || 'light';
        this.setTheme(savedTheme);
    },
    
    // Set theme
    setTheme(theme) {
        this.currentTheme = theme;
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('arcanopolis-theme', theme);
        
        // Update theme toggle icon
        const themeIcon = document.querySelector('.theme-icon');
        if (themeIcon) {
            themeIcon.textContent = theme === 'light' ? '◐' : '☀';
        }
    },
    
    // Toggle between light and dark theme
    toggleTheme() {
        const newTheme = this.currentTheme === 'light' ? 'dark' : 'light';
        this.setTheme(newTheme);
    },
    
    // Load all markdown data
    async loadData() {
        try {
            console.log('🔄 Loading content data...');
            
            // Load all content using the new ContentLoader
            const allContent = await ContentLoader.loadAllContent();
            
            if (allContent.length === 0) {
                console.warn('No content loaded');
                return;
            }
            
            // Organize content by category
            this.data.clear();
            allContent.forEach(item => {
                if (!this.data.has(item.category)) {
                    this.data.set(item.category, []);
                }
                this.data.get(item.category).push(item);
            });
            
            // Initialize cross-references
            CrossReferenceManager.init(allContent);
            
            // Build search index
            this.buildSearchIndex();
            
            // Update featured content
            this.updateFeaturedContent();
            
            console.log('✅ Content loading complete');
            
        } catch (error) {
            console.error('Error loading data:', error);
            this.showError('Failed to load archive data. Please check your markdown files.');
        }
    },
    
    // Build search index using Fuse.js
    buildSearchIndex() {
        if (typeof Fuse === 'undefined') {
            console.warn('Fuse.js not loaded, search will be limited');
            return;
        }
        
        const allItems = [];
        for (const [category, items] of this.data) {
            items.forEach(item => {
                allItems.push({
                    ...item,
                    category: category
                });
            });
        }
        
        this.searchIndex = new Fuse(allItems, {
            keys: [
                { name: 'title', weight: 0.7 },
                { name: 'rawContent', weight: 0.3 },
                { name: 'tags', weight: 0.2 },
                { name: 'type', weight: 0.1 }
            ],
            threshold: 0.3,
            includeScore: true,
            ignoreLocation: true,
            findAllMatches: true
        });
        
        console.log(`🔍 Search index built with ${allItems.length} items`);
    },
    
    // Perform search from hero section
    performHeroSearch() {
        const searchInput = document.getElementById('heroSearch');
        if (searchInput && searchInput.value.trim()) {
            const query = searchInput.value.trim();
            window.location.href = `search.html?q=${encodeURIComponent(query)}`;
        }
    },
    
    // Focus search input
    focusSearch() {
        const searchInput = document.getElementById('heroSearch') || 
                           document.getElementById('searchInput');
        if (searchInput) {
            searchInput.focus();
        }
    },
    
    // Update featured content on homepage
    updateFeaturedContent() {
        const featuredContainer = document.getElementById('featuredContent');
        if (!featuredContainer) return;
        
        // Get random featured items
        const featuredItems = this.getFeaturedItems(3);
        
        if (featuredItems.length === 0) {
            return; // Keep placeholder content
        }
        
        featuredContainer.innerHTML = featuredItems.map(item => 
            this.createContentCard(item)
        ).join('');
    },
    
    // Get featured items (random selection)
    getFeaturedItems(count = 3) {
        const allItems = [];
        for (const [category, items] of this.data) {
            allItems.push(...items);
        }
        
        // Shuffle and take first count items
        const shuffled = allItems.sort(() => 0.5 - Math.random());
        return shuffled.slice(0, count);
    },
    
    // Create content card HTML
    createContentCard(item) {
        const excerpt = this.truncateText(item.rawContent || item.content || '', 150);
        const categoryLabel = this.getCategoryLabel(item.category);
        const tags = item.tags ? item.tags.join(',') : '';
        
        return `
            <div class="content-card" data-id="${item.id}" data-category="${item.category}" data-tags="${tags}">
                <div class="card-header">
                    <span class="card-category">${categoryLabel}</span>
                    ${item.type ? `<span class="card-type">${item.type}</span>` : ''}
                </div>
                <h4 class="card-title">
                    <a href="content.html?id=${item.id}">${item.title}</a>
                </h4>
                <p class="card-excerpt">${excerpt}</p>
                <div class="card-footer">
                    <span class="card-meta">${this.formatDate(item.date)}</span>
                    ${item.tags && item.tags.length > 0 ? 
                        `<div class="card-tags">${item.tags.slice(0, 3).map(tag => `<span class="tag">${tag}</span>`).join('')}</div>` : 
                        ''
                    }
                </div>
            </div>
        `;
    },
    
    // Get category display label
    getCategoryLabel(category) {
        const labels = {
            'lore': 'Lore',
            'locations': 'Location',
            'npcs': 'Character',
            'factions': 'Faction',
            'items': 'Item',
            'handouts': 'Handout',
            'containment': 'Containment'
        };
        return labels[category] || category;
    },
    
    // Truncate text to specified length
    truncateText(text, maxLength) {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    },
    
    // Format date for display
    formatDate(dateString) {
        if (!dateString) return 'No date';
        
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    },
    
    // Initialize page-specific functionality
    initializePage() {
        const path = window.location.pathname;
        const page = path.split('/').pop().replace('.html', '') || 'index';
        
        this.currentPage = page;
        
        // Initialize page-specific features
        switch (page) {
            case 'browse':
                this.initializeBrowsePage();
                break;
            case 'search':
                this.initializeSearchPage();
                break;
            case 'timeline':
                this.initializeTimelinePage();
                break;
            case 'relationships':
                this.initializeRelationshipsPage();
                break;
        }
    },
    
    // Initialize browse page
    initializeBrowsePage() {
        this.loadBrowseContent();
        this.setupBrowseFilters();
    },
    
    // Initialize search page
    initializeSearchPage() {
        this.setupSearchInterface();
        this.loadSearchFromURL();
    },
    
    // Initialize timeline page
    initializeTimelinePage() {
        if (typeof Timeline !== 'undefined') {
            Timeline.init();
        }
    },
    
    // Initialize relationships page
    initializeRelationshipsPage() {
        if (typeof Relationships !== 'undefined') {
            Relationships.init();
        }
    },
    
    // Load browse page content
    loadBrowseContent() {
        const contentGrid = document.getElementById('contentGrid');
        if (!contentGrid) return;
        
        const allItems = [];
        for (const [category, items] of this.data) {
            allItems.push(...items.map(item => ({ ...item, category })));
        }
        
        if (allItems.length === 0) {
            // Show loading message
            contentGrid.innerHTML = `
                <div class="content-card placeholder">
                    <div class="card-header">
                        <span class="card-category">Loading</span>
                    </div>
                    <h4 class="card-title">Loading content...</h4>
                    <p class="card-excerpt">Please wait while we load the archive content.</p>
                </div>
            `;
            return;
        }
        
        contentGrid.innerHTML = allItems.map(item => 
            this.createContentCard(item)
        ).join('');
        
        this.updateResultsCount(allItems.length);
        this.updateTagFilter(allItems);
    },
    
    // Setup browse page filters
    setupBrowseFilters() {
        // Category filter
        const categoryLinks = document.querySelectorAll('.category-list a');
        categoryLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const category = link.dataset.category;
                this.filterByCategory(category);
                
                // Update active state
                categoryLinks.forEach(l => l.classList.remove('active'));
                link.classList.add('active');
            });
        });
        
        // View toggle
        const viewButtons = document.querySelectorAll('.view-btn');
        viewButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                const view = btn.dataset.view;
                this.toggleView(view);
                
                viewButtons.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
            });
        });
        
        // Sort select
        const sortSelect = document.getElementById('sortSelect');
        if (sortSelect) {
            sortSelect.addEventListener('change', (e) => {
                this.sortContent(e.target.value);
            });
        }
    },
    
    // Filter content by category
    filterByCategory(category) {
        const contentGrid = document.getElementById('contentGrid');
        if (!contentGrid) return;
        
        const cards = contentGrid.querySelectorAll('.content-card');
        let visibleCount = 0;
        
        cards.forEach(card => {
            const cardCategory = card.dataset.category;
            const shouldShow = category === 'all' || cardCategory === category;
            
            card.style.display = shouldShow ? 'block' : 'none';
            if (shouldShow) visibleCount++;
        });
        
        this.updateResultsCount(visibleCount);
        this.updateBreadcrumbs(category);
    },
    
    // Toggle between grid and list view
    toggleView(view) {
        const contentGrid = document.getElementById('contentGrid');
        if (!contentGrid) return;
        
        contentGrid.className = `content-grid ${view}-view`;
    },
    
    // Sort content
    sortContent(sortBy) {
        const contentGrid = document.getElementById('contentGrid');
        if (!contentGrid) return;
        
        const cards = Array.from(contentGrid.querySelectorAll('.content-card'));
        
        cards.sort((a, b) => {
            switch (sortBy) {
                case 'title-asc':
                    return a.querySelector('.card-title').textContent.localeCompare(
                        b.querySelector('.card-title').textContent
                    );
                case 'title-desc':
                    return b.querySelector('.card-title').textContent.localeCompare(
                        a.querySelector('.card-title').textContent
                    );
                case 'date-new':
                    return new Date(b.dataset.date) - new Date(a.dataset.date);
                case 'date-old':
                    return new Date(a.dataset.date) - new Date(b.dataset.date);
                default:
                    return 0;
            }
        });
        
        // Re-append sorted cards
        cards.forEach(card => contentGrid.appendChild(card));
    },
    
    // Update results count
    updateResultsCount(count) {
        const resultsCount = document.getElementById('resultsCount');
        if (resultsCount) {
            resultsCount.textContent = `${count} ${count === 1 ? 'entry' : 'entries'} found`;
        }
    },
    
    // Update breadcrumbs
    updateBreadcrumbs(category) {
        const breadcrumbs = document.getElementById('breadcrumbs');
        if (!breadcrumbs) return;
        
        const categoryLabel = this.getCategoryLabel(category);
        const currentSpan = breadcrumbs.querySelector('.current');
        if (currentSpan) {
            currentSpan.textContent = category === 'all' ? 'Browse' : categoryLabel;
        }
    },
    
    // Update tag filter with all available tags
    updateTagFilter(allItems) {
        const tagFilter = document.getElementById('tagFilter');
        if (!tagFilter) return;
        
        // Collect all unique tags
        const allTags = new Set();
        allItems.forEach(item => {
            if (item.tags) {
                item.tags.forEach(tag => allTags.add(tag));
            }
        });
        
        // Create tag filter HTML
        const tagHtml = Array.from(allTags).sort().map(tag => 
            `<button class="tag-filter-btn" data-tag="${tag}">${tag}</button>`
        ).join('');
        
        tagFilter.innerHTML = tagHtml;
        
        // Add click handlers for tag filtering
        tagFilter.querySelectorAll('.tag-filter-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const tag = btn.dataset.tag;
                this.filterByTag(tag);
                
                // Update active state
                tagFilter.querySelectorAll('.tag-filter-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
            });
        });
    },
    
    // Filter content by tag
    filterByTag(tag) {
        const contentGrid = document.getElementById('contentGrid');
        if (!contentGrid) return;
        
        const cards = contentGrid.querySelectorAll('.content-card');
        let visibleCount = 0;
        
        cards.forEach(card => {
            const cardTags = card.dataset.tags ? card.dataset.tags.split(',') : [];
            const shouldShow = cardTags.includes(tag);
            
            card.style.display = shouldShow ? 'block' : 'none';
            if (shouldShow) visibleCount++;
        });
        
        this.updateResultsCount(visibleCount);
    },
    
    // Setup search interface
    setupSearchInterface() {
        const searchInput = document.getElementById('searchInput');
        const searchButton = document.getElementById('searchButton');
        const filterToggle = document.getElementById('filterToggle');
        const filterPanel = document.getElementById('filterPanel');
        
        if (searchInput && searchButton) {
            searchButton.addEventListener('click', () => this.performSearch());
            searchInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') this.performSearch();
            });
            
            // Real-time search suggestions
            searchInput.addEventListener('input', (e) => {
                this.showSearchSuggestions(e.target.value);
            });
        }
        
        if (filterToggle && filterPanel) {
            filterToggle.addEventListener('click', () => {
                filterPanel.style.display = filterPanel.style.display === 'none' ? 'block' : 'none';
                filterToggle.classList.toggle('active');
            });
        }
    },
    
    // Load search from URL parameters
    loadSearchFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get('q');
        
        if (query) {
            const searchInput = document.getElementById('searchInput');
            if (searchInput) {
                searchInput.value = query;
                this.performSearch();
            }
        }
    },
    
    // Perform search
    performSearch() {
        const searchInput = document.getElementById('searchInput');
        if (!searchInput) return;
        
        const query = searchInput.value.trim();
        if (!query) return;
        
        this.showSearchResults(query);
        this.saveRecentSearch(query);
    },
    
    // Show search results
    showSearchResults(query) {
        const resultsContainer = document.getElementById('resultsContainer');
        const resultsHeader = document.getElementById('resultsHeader');
        const resultsTitle = document.getElementById('resultsTitle');
        const resultsCount = document.getElementById('resultsCount');
        
        if (!resultsContainer) return;
        
        let results = [];
        
        if (this.searchIndex) {
            const fuseResults = this.searchIndex.search(query);
            results = fuseResults.map(result => result.item);
        } else {
            // Fallback simple search
            results = this.simpleSearch(query);
        }
        
        if (results.length === 0) {
            resultsContainer.innerHTML = `
                <div class="search-welcome">
                    <div class="welcome-icon">🔍</div>
                    <h3>No Results Found</h3>
                    <p>No entries match your search for "${query}". Try different keywords or check your spelling.</p>
                </div>
            `;
        } else {
            resultsContainer.innerHTML = results.map(item => 
                this.createContentCard(item)
            ).join('');
        }
        
        // Update results header
        if (resultsHeader) resultsHeader.style.display = 'block';
        if (resultsTitle) resultsTitle.textContent = `Search Results for "${query}"`;
        if (resultsCount) resultsCount.textContent = `${results.length} ${results.length === 1 ? 'result' : 'results'} found`;
    },
    
    // Simple search fallback
    simpleSearch(query) {
        const allItems = [];
        for (const [category, items] of this.data) {
            allItems.push(...items.map(item => ({ ...item, category })));
        }
        
        const lowerQuery = query.toLowerCase();
        return allItems.filter(item => 
            item.title.toLowerCase().includes(lowerQuery) ||
            (item.content && item.content.toLowerCase().includes(lowerQuery)) ||
            (item.tags && item.tags.some(tag => tag.toLowerCase().includes(lowerQuery)))
        );
    },
    
    // Show search suggestions
    showSearchSuggestions(query) {
        const suggestionsContainer = document.getElementById('searchSuggestions');
        if (!suggestionsContainer || query.length < 2) {
            if (suggestionsContainer) suggestionsContainer.style.display = 'none';
            return;
        }
        
        const suggestions = this.getSearchSuggestions(query);
        
        if (suggestions.length === 0) {
            suggestionsContainer.style.display = 'none';
            return;
        }
        
        suggestionsContainer.innerHTML = suggestions.map(suggestion => 
            `<div class="suggestion-item" data-query="${suggestion}">${suggestion}</div>`
        ).join('');
        
        suggestionsContainer.style.display = 'block';
        
        // Add click handlers
        suggestionsContainer.querySelectorAll('.suggestion-item').forEach(item => {
            item.addEventListener('click', () => {
                const searchInput = document.getElementById('searchInput');
                if (searchInput) {
                    searchInput.value = item.dataset.query;
                    this.performSearch();
                }
            });
        });
    },
    
    // Get search suggestions
    getSearchSuggestions(query) {
        const allItems = [];
        for (const [category, items] of this.data) {
            allItems.push(...items);
        }
        
        const lowerQuery = query.toLowerCase();
        const suggestions = new Set();
        
        allItems.forEach(item => {
            if (item.title.toLowerCase().includes(lowerQuery)) {
                suggestions.add(item.title);
            }
            if (item.tags) {
                item.tags.forEach(tag => {
                    if (tag.toLowerCase().includes(lowerQuery)) {
                        suggestions.add(tag);
                    }
                });
            }
        });
        
        return Array.from(suggestions).slice(0, 5);
    },
    
    // Save recent search
    saveRecentSearch(query) {
        let recentSearches = JSON.parse(localStorage.getItem('arcanopolis-recent-searches') || '[]');
        recentSearches = recentSearches.filter(search => search !== query);
        recentSearches.unshift(query);
        recentSearches = recentSearches.slice(0, 5);
        
        localStorage.setItem('arcanopolis-recent-searches', JSON.stringify(recentSearches));
        this.updateRecentSearches();
    },
    
    // Update recent searches display
    updateRecentSearches() {
        const recentSearches = document.getElementById('recentSearches');
        const recentSearchChips = document.getElementById('recentSearchChips');
        
        if (!recentSearches || !recentSearchChips) return;
        
        const searches = JSON.parse(localStorage.getItem('arcanopolis-recent-searches') || '[]');
        
        if (searches.length === 0) {
            recentSearches.style.display = 'none';
            return;
        }
        
        recentSearchChips.innerHTML = searches.map(search => 
            `<button class="search-chip" data-query="${search}">${search}</button>`
        ).join('');
        
        recentSearches.style.display = 'block';
        
        // Add click handlers
        recentSearchChips.querySelectorAll('.search-chip').forEach(chip => {
            chip.addEventListener('click', () => {
                const searchInput = document.getElementById('searchInput');
                if (searchInput) {
                    searchInput.value = chip.dataset.query;
                    this.performSearch();
                }
            });
        });
    },
    
    // Show error message
    showError(message) {
        console.error(message);
        // You could implement a toast notification system here
    }
};

// Initialize the application when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    App.init();
});

// Export for use in other modules
window.App = App;
